export type PriceListColumn =
  | "status"
  | "unit"
  | "type"
  | "plan"
  | "area"
  | "price"
  | "pricePerSf";

export type ColumnControl = "text" | "range" | "dropdown";

export function columnControl(key: PriceListColumn): ColumnControl {
  switch (key) {
    case "area":
    case "price":
    case "pricePerSf":
      return "range";
    case "status":
    case "plan":
    case "type":
      return "dropdown";
    default:
      return "text";
  }
}

/**
 * Supports comparison operators, numeric ranges, and k/m shorthand.
 * A bare number remains an exact match with a small rounding tolerance.
 */
export function matchesRange(expression: string, value: number): boolean {
  const normalized = expression
    .trim()
    .replace(/[$,\s]/g, "")
    .replace(/(\d+(?:\.\d+)?)m/gi, (_, number) =>
      String(parseFloat(number) * 1_000_000),
    )
    .replace(/(\d+(?:\.\d+)?)k/gi, (_, number) =>
      String(parseFloat(number) * 1_000),
    );

  if (!normalized) return true;

  const comparison = normalized.match(/^(<=|>=|<|>)(-?\d+(?:\.\d+)?)$/);
  if (comparison) {
    const number = parseFloat(comparison[2]);
    switch (comparison[1]) {
      case "<":
        return value < number;
      case "<=":
        return value <= number;
      case ">":
        return value > number;
      case ">=":
        return value >= number;
    }
  }

  const range = normalized.match(
    /^(-?\d+(?:\.\d+)?)(?:-|\.\.)(-?\d+(?:\.\d+)?)$/,
  );
  if (range) {
    const start = parseFloat(range[1]);
    const end = parseFloat(range[2]);
    return value >= Math.min(start, end) && value <= Math.max(start, end);
  }

  const exact = parseFloat(normalized);
  if (!Number.isNaN(exact)) return Math.abs(value - exact) < 0.5;

  return value.toString().includes(normalized);
}
